import { useCallback, useState } from "react";
import { object, string } from "yup";

import {
  IOrderCancelled,
  IOrderConfirmed,
  IOrderDelivered,
  IOrderError,
  IOrderHandling,
  IOrderInvoiced,
  IOrderPaid,
  IOrderShipped,
} from "@/interfaces/order-status.interfaces";
import { IOrderInput } from "@/interfaces/order.interfaces";
import { OrderDataTabMessages } from "@/messages/order-form-page/order-data-tab.messages";
import { OrderStatus } from "@/types/order.types";

import useCancelOrder from "./use-cancel-order";
import useConfirmOrder from "./use-confirm-order";
import useDeliverOrder from "./use-deliver-order";
import useInvoiceOrder from "./use-invoice-order";
import usePayOrder from "./use-pay-order";
import useReportOrderError from "./use-report-order-error";
import useSetOrderHandling from "./use-set-order-handling";
import useShipOrder from "./use-ship-order";

export const buildOrderDataTabFormSchema = (messages: OrderDataTabMessages) => {
  const baseSchema = object().shape({
    shippingDocument: string().required(messages.errorRequiredShippingDocument),

    shippingName: string().required(messages.errorRequiredShippingName),
    shippingPhoneNumber: string().required(messages.errorRequiredPhone),

    shippingAddressZipcode: string().required(messages.errorRequiredZipCode),
    shippingAddress: string().required(messages.errorRequiredAddress),
    shippingAddressNumber: string().required(messages.errorRequiredAddressNumber),
    shippingAddressDistrict: string().required(messages.errorRequiredDistrict),
    shippingAddressCity: string().required(messages.errorRequiredCity),
    shippingAddressState: string().required(messages.errorRequiredState),
    shippingAddressCountry: string().required(messages.errorRequiredCountry),
  });

  return baseSchema;
};

export const normalizeOrderConfirmedData = (formData: IOrderInput): IOrderConfirmed => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
  };
};

export const normalizeOrderHandlingData = (formData: IOrderInput): IOrderHandling => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
  };
};

export const normalizeOrderPaidData = (formData: IOrderInput): IOrderPaid => {
  const payment = formData.payments[0];
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
    issueDate: payment.issueDate,
    installmentNo: payment.installmentNo || "1",
    dueDate: payment.dueDate,
    paymentDate: payment.paymentDate,
    paymentMethodType: payment.paymentMethodType,
    paymentMethodName: payment.paymentMethodName,
    paymentAmount: Number(payment.paymentAmount),
  };
};

export const normalizeOrderInvoicedData = (formData: IOrderInput): IOrderInvoiced => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  const invoiceDate = formData.invoiceDate ? formData.invoiceDate : new Date().toISOString();

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
    invoiceDate: invoiceDate,
    invoiceNumber: formData.invoiceNumber,
    invoiceSeries: formData.invoiceSeries,
    invoiceKey: formData.invoiceKey,
    invoiceXml: formData.invoiceXml,
  };
};

export const normalizeOrderShippedData = (formData: IOrderInput): IOrderShipped => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  const shippingDate = formData.shippingDate ? formData.shippingDate : new Date().toISOString();

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
    shippingVolumeType: formData.shippingVolumeType,
    shippingVolumes: formData.shippingVolumes,
    shippingObject: formData.shippingObject,
    shippingTrackingUrl: formData.shippingTrackingUrl || null,
    shippingDate: shippingDate,
  };
};

export const normalizeOrderDeliveredData = (formData: IOrderInput): IOrderDelivered => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;
  const deliveryDate = formData.deliveryDate ? formData.deliveryDate : new Date().toISOString();

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
    deliveryDate: deliveryDate,
  };
};

export const normalizeOrderCancelledData = (formData: IOrderInput): IOrderCancelled => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
    cancelationDate: new Date().toISOString(),
    fraudRisk: false,
  };
};

export const normalizeOrderErrorData = (formData: IOrderInput): IOrderError => {
  const { orderNumber, customerPlatformId } = formData;

  const formattedOrderNumber = String(orderNumber);
  const formattedCustomerPlatformId = customerPlatformId ? customerPlatformId : null;

  return {
    orderDetail: {
      orderNumber: formattedOrderNumber,
      customerPlatformId: formattedCustomerPlatformId,
    },
    notes: formData.orderComments1 || "Erro não especificado",
  };
};

const useHandleOrderStatusChange = () => {
  const [error, setError] = useState<string | null>(null);

  const { create: createConfirmOrder, isLoading: isLoadingConfirmOrder } = useConfirmOrder();
  const { create: createPayOrder, isLoading: isLoadingPayOrder } = usePayOrder();
  const { create: createHandlingOrder, isLoading: isLoadingHandlingOrder } = useSetOrderHandling();
  const { create: createInvoiceOrder, isLoading: isLoadingInvoiceOrder } = useInvoiceOrder();
  const { create: createShipOrder, isLoading: isLoadingShipOrder } = useShipOrder();
  const { create: createDeliverOrder, isLoading: isLoadingDeliverOrder } = useDeliverOrder();
  const { create: createCancelOrder, isLoading: isLoadingCancelOrder } = useCancelOrder();
  const { create: createOrderError, isLoading: isLoadingOrderError } = useReportOrderError();

  const isLoading =
    isLoadingConfirmOrder ||
    isLoadingPayOrder ||
    isLoadingHandlingOrder ||
    isLoadingInvoiceOrder ||
    isLoadingShipOrder ||
    isLoadingDeliverOrder ||
    isLoadingCancelOrder ||
    isLoadingOrderError;

  const handleOrderStatusChange = useCallback(
    async (formData: IOrderInput, messages: OrderDataTabMessages) => {
      const { orderUpdateError } = messages;
      setError(null);

      try {
        switch (formData.orderStatus) {
          case OrderStatus.Confirmed: {
            const confirmedData = normalizeOrderConfirmedData(formData);

            await createConfirmOrder({ formData: confirmedData });
            break;
          }
          case OrderStatus.Paid: {
            const paidData = normalizeOrderPaidData(formData);

            await createPayOrder({ formData: paidData });
            break;
          }
          case OrderStatus.Handling: {
            const handlingData = normalizeOrderHandlingData(formData);

            await createHandlingOrder({ formData: handlingData });
            break;
          }
          case OrderStatus.Invoiced: {
            const invoicedData = normalizeOrderInvoicedData(formData);

            await createInvoiceOrder({ formData: invoicedData });
            break;
          }
          case OrderStatus.Shipped: {
            const shippedData = normalizeOrderShippedData(formData);

            await createShipOrder({ formData: shippedData });
            break;
          }
          case OrderStatus.Delivered: {
            const deliveredData = normalizeOrderDeliveredData(formData);

            await createDeliverOrder({ formData: deliveredData });
            break;
          }
          case OrderStatus.Cancelled: {
            const cancelledData = normalizeOrderCancelledData(formData);

            await createCancelOrder({ formData: cancelledData });
            break;
          }
          case OrderStatus.Error: {
            const errorData = normalizeOrderErrorData(formData);

            await createOrderError({ formData: errorData });
            break;
          }
          default:
            throw new Error("Status desconhecido");
        }
      } catch (error) {
        setError(orderUpdateError);
        throw new Error(orderUpdateError);
      }
    },
    [
      createConfirmOrder,
      createPayOrder,
      createHandlingOrder,
      createInvoiceOrder,
      createShipOrder,
      createDeliverOrder,
      createCancelOrder,
      createOrderError,
      setError,
    ]
  );

  return { handleOrderStatusChange, isLoading, error };
};

export default useHandleOrderStatusChange;
